import { useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useKeycloak } from "@react-keycloak/web";
import { setUiVersion, UiVersion } from "../../uiVersion";
import { mapPathAcrossVersions } from "./versionPathMapping";
import { getRoutesByIntegration } from "../../api/routes/routesApi";

// GUNDI-5642: the one place that performs a version switch, shared by the
// banner (new → legacy) and the hidden unlock modal (legacy → new) so the two
// directions cannot drift apart. New code, not a frozen import, so it may use
// the CURRENT api modules for the path translation.
export function useVersionSwitch() {
    const { keycloak } = useKeycloak();
    const navigate = useNavigate();
    const { pathname } = useLocation();
    const [switching, setSwitching] = useState(false);

    const validToken = () => {
        const token = keycloak?.token;
        return token && typeof token === "string" && !keycloak.isTokenExpired() ? token : null;
    };

    const switchTo = async (target: UiVersion) => {
        setSwitching(true);
        const token = validToken();
        const destination = await mapPathAcrossVersions(pathname, target, {
            providerHasRoute: async (providerId) => {
                if (!token) return false;
                const { results, ok } = await getRoutesByIntegration(token, "provider", providerId);
                // A failed lookup is not "none": say no and land the user on the
                // connection page, which links to the route one click away,
                // rather than on a route page that may not exist.
                return ok && results.length > 0;
            },
        });
        // Flip the flag first: the shell re-renders the other tree, and the
        // navigate below lands it on a URL in that tree's vocabulary.
        setUiVersion(target);
        navigate(destination, { replace: true });
        setSwitching(false);
    };

    return { switchTo, switching };
}
